import { Component, inject } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { CommonService } from '../../services/common.service';
import { UpiQrDialogComponent } from '../../common/components/upi-qr-dialog';

@Component({
  selector: 'app-subscription-banner',
  standalone: true,
  imports: [CommonModule, TooltipModule, ButtonModule, UpiQrDialogComponent],
  template: `
    @if (commonService.isExpiringSoon()) {
      <div class="subscription-banner subscription-banner--warning">
        <span class="subscription-banner__text">
          @if (isDemo) {
            Demo expires in {{ daysRemaining }} {{ daysLabel }} (ends {{ endDate | date:'dd MMM yyyy' }}).
          } @else {
            Subscription expires in {{ daysRemaining }} {{ daysLabel }} (ends {{ endDate | date:'dd MMM yyyy' }}).
            Contact your administrator to renew.
          }
        </span>
        @if (veerUpiId && !isDemo) {
          <p-button label="Pay via QR" size="small" [text]="true" (onClick)="showQr = true" />
        }
        <button
          type="button"
          class="subscription-banner__info"
          [pTooltip]="expiringSoonTooltip"
          tooltipPosition="bottom"
          aria-label="Subscription renewal details"
        >
          <i class="pi pi-info-circle" aria-hidden="true"></i>
        </button>
      </div>
    } @else if (commonService.isReadOnly()) {
      <div class="subscription-banner subscription-banner--danger">
        <span class="subscription-banner__text">
          Subscription expired on {{ endDate | date:'dd MMM yyyy' }}.
          Read-only mode is active until {{ graceEndDate | date:'dd MMM yyyy' }}.
          Renew to restore full access.
        </span>
        @if (veerUpiId) {
          <p-button label="Pay via QR" size="small" [text]="true" (onClick)="showQr = true" />
        }
        <button
          type="button"
          class="subscription-banner__info"
          [pTooltip]="readOnlyTooltip"
          tooltipPosition="bottom"
          aria-label="Subscription renewal details"
        >
          <i class="pi pi-info-circle" aria-hidden="true"></i>
        </button>
      </div>
    }

    <app-upi-qr-dialog
      [(visible)]="showQr"
      [heading]="'VMS ' + (commonService.clientSubscriptionStatus?.subscriptionMode ?? '') + ' Renewal'"
      [upiId]="veerUpiId"
      [merchantName]="commonService.clientSubscriptionStatus?.veerUpiMerchantName"
      [amount]="commonService.clientSubscriptionStatus?.amcAmount"
      [note]="renewalNote"
      notConfiguredMessage="UPI payments are not available yet. Please contact Veer Solutions support to renew."
    />
  `,
  styles: [`
    .subscription-banner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      font-weight: 600;
      text-align: center;
    }

    .subscription-banner__text {
      line-height: 1.4;
    }

    .subscription-banner__info {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      border: none;
      background: transparent;
      padding: 0.15rem;
      cursor: help;
      font-size: 1rem;
      line-height: 1;
      opacity: 0.85;
    }

    .subscription-banner__info:hover,
    .subscription-banner__info:focus-visible {
      opacity: 1;
    }

    .subscription-banner--warning {
      background: #fff3cd;
      color: #664d03;
      border-bottom: 1px solid #ffecb5;
    }

    .subscription-banner--warning .subscription-banner__info {
      color: #664d03;
    }

    .subscription-banner--danger {
      background: #f8d7da;
      color: #842029;
      border-bottom: 1px solid #f5c2c7;
    }

    .subscription-banner--danger .subscription-banner__info {
      color: #842029;
    }
  `]
})
export class SubscriptionBanner {
  commonService = inject(CommonService);
  showQr = false;

  get veerUpiId(): string | null | undefined {
    return this.commonService.clientSubscriptionStatus?.veerUpiId;
  }

  get isDemo(): boolean {
    return this.commonService.clientSubscriptionStatus?.planCode === 'demo';
  }

  get renewalNote(): string {
    const prefix = this.commonService.clientSubscriptionStatus?.veerUpiNotePrefix || 'VMS Renewal';
    const clientName = this.commonService.clientDetail?.clientName;
    return clientName ? `${prefix} - ${clientName}` : prefix;
  }

  get displayState() {
    return this.commonService.getSubscriptionDisplayState();
  }

  get daysRemaining(): number {
    return this.displayState?.daysRemaining ?? this.commonService.clientSubscriptionStatus?.daysRemaining ?? 0;
  }

  get daysLabel(): string {
    return this.daysRemaining === 1 ? 'day' : 'days';
  }

  get endDate(): string | undefined {
    return this.displayState?.subscriptionEndUtc ?? this.commonService.clientSubscriptionStatus?.subscriptionEndUtc;
  }

  get graceEndDate(): string | undefined {
    return this.displayState?.graceEndUtc ?? this.commonService.clientSubscriptionStatus?.graceEndUtc;
  }

  get expiringSoonTooltip(): string {
    const end = this.formatDisplayDate(this.endDate);
    const graceEnd = this.formatDisplayDate(this.graceEndDate);
    return `After ${end}, the app switches to read-only mode (you can sign in and view data, but cannot save changes) until ${graceEnd}. If the subscription is not renewed by ${graceEnd}, login will be disabled.`;
  }

  get readOnlyTooltip(): string {
    const graceEnd = this.formatDisplayDate(this.graceEndDate);
    return `Saving changes is disabled. You can still sign in and view existing data until ${graceEnd}. After ${graceEnd}, login will be disabled until your subscription is renewed. Contact your administrator.`;
  }

  private formatDisplayDate(value: string | undefined): string {
    if (!value) {
      return 'the grace end date';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'the grace end date';
    }

    return formatDate(date, 'dd MMM yyyy', 'en-IN', 'UTC');
  }
}
